/**
 * The markers a reference can be headed with, and how each one is drawn in three dimensions.
 *
 * The vocabulary is the package's own rather than the API's `ArrowType`, for the reason
 * `NetworkLineStyle` is: a chart package that named its inputs after one host's enum would be a
 * chart package only that host could use. The host maps its enum onto this at the boundary.
 *
 * What decides how a glyph can be drawn is its fill, and the 2D artwork splits three ways:
 *
 * - **Filled.** A solid black shape, which has an obvious solid of revolution behind it: a cone, an
 *   octahedron, a sphere. These are real geometry and read from every angle.
 * - **Outlined.** A white fill with a dark stroke, meaning *hollow* - the hollow triangle and the
 *   hollow diamond. The same bodies painted in the surface color with the rim darkened, which is
 *   how a reader sees "empty" from any direction, and how the region pass already says where a
 *   group ends.
 * - **Planar.** Crow's feet, half arrows, tick bars and the open chevron: strokes with no interior,
 *   and no canonical roll about the reference to draw them at. Rotate a crow's foot ninety degrees
 *   about its own line and it is a single stroke. These are notation rather than objects, so they
 *   are billboarded - which this renderer already does for its marks, regions and labels.
 *
 * None of them takes the reference's own color. The 2D glyphs hard-code one ink and one fill and
 * the legend already carries the type's color on the line itself, so a marker repeating it would
 * put a saturated shape at every junction to say something that has already been said.
 */

/** A marker's shape, independent of whether it is filled or hollow. */
export type NetworkMarkerBody = 'cone' | 'octahedron' | 'sphere';

export type NetworkMarker =
  | { kind: 'none' }
  | { kind: 'solid'; body: NetworkMarkerBody; isHollow: boolean }
  /** Drawn from the 2D artwork, facing the camera. `glyph` indexes the atlas. */
  | { kind: 'planar'; glyph: NetworkMarkerGlyph };

/**
 * The flat glyphs, in the order the atlas lays them out.
 *
 * One declaration, with the union derived from it. Declared twice - a union *and* a list - a glyph
 * could be given artwork and left out of the list, and nothing would fail: `toMarkerCellOffset`
 * answers `-1 / length` for a name it cannot find, and the atlas simply never draws it. The
 * shader's cell count is read off the length here too, for the same reason.
 */
export const NETWORK_MARKER_GLYPHS = [
  /** `default`: one short slash across the line. */
  'slash',
  /** `both`: the open chevron, two strokes and no fill. */
  'chevron',
  'halfTop',
  'halfBottom',
  'one',
  'oneMandatory',
  'zeroOrOne',
  'many',
  'oneOrMany',
  'zeroOrMany',
] as const;

export type NetworkMarkerGlyph = (typeof NETWORK_MARKER_GLYPHS)[number];

export const NO_MARKER: NetworkMarker = { kind: 'none' };
